/**
 * Blog image studio — shared, article-scoped image tooling.
 *
 * Extracted from the former single-page BlogV2ArticleDetail so the new
 * topic-centric workspace (BlogTopicDetail) and the legacy article view can
 * both reuse it. Two pieces:
 *
 *   - HeroStudioModal — user-driven hero image: simple instruction → LLM crafts
 *     an editable prompt → generate (background) → optional transform on the
 *     latest → adopt as hero. Centered modal (no side drawer).
 *   - ImageEditPanel — a deliberate exception to the "modals centered" rule
 *     (the user asked for a right rail image editor): edit ONE in-article image.
 *
 * All state and API calls live here; callers pass the articleId and reload
 * callbacks. English-only chrome (Atlas/blog doctrine).
 */
import { useEffect, useState } from "react";
import { Loader2, Sparkles, Wand2, X } from "lucide-react";
import { api } from "../../api";
import { Button, Modal } from "../../ui";
import { useToast } from "../../ui/toast";

const SELECT_CLS =
  "rounded-lg border border-cream-300 dark:border-ink-600 bg-white dark:bg-ink-800 px-3 py-2 text-sm";

export function StepNo({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-600 text-white text-[11px] font-bold">
      {n}
    </span>
  );
}

export function HeroStudioModal({
  articleId,
  articleTitle,
  open,
  onClose,
  onAdopted,
}: {
  articleId: string;
  articleTitle: string;
  open: boolean;
  onClose: () => void;
  onAdopted: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [bgInstruction, setBgInstruction] = useState("");
  const [bgSuggesting, setBgSuggesting] = useState(false);
  const [bgPrompt, setBgPrompt] = useState("");
  const [bgCrafting, setBgCrafting] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);
  const [tfInstruction, setTfInstruction] = useState("");
  const [tfPrompt, setTfPrompt] = useState("");
  const [tfCrafting, setTfCrafting] = useState(false);
  const [tfBusy, setTfBusy] = useState(false);
  const [versions, setVersions] = useState<{ url: string; version: string }[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [adopting, setAdopting] = useState(false);

  // On open: reset + prefill the step-1 instruction from the article core.
  useEffect(() => {
    if (!open) return;
    setBgInstruction("");
    setBgPrompt("");
    setTfInstruction("");
    setTfPrompt("");
    setVersions([]);
    setSelectedIdx(-1);
    let cancelled = false;
    (async () => {
      setBgSuggesting(true);
      try {
        const d = await api.post<{ instruction: string }>(
          `/api/blog/v2/articles/${articleId}/hero/suggest-instruction`,
        );
        if (!cancelled) setBgInstruction((cur) => (cur.trim() ? cur : d.instruction || ""));
      } catch {
        /* non-blocking */
      } finally {
        if (!cancelled) setBgSuggesting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, articleId]);

  async function craftHeroPrompt(step: "background" | "transform") {
    const instruction = step === "background" ? bgInstruction : tfInstruction;
    if (!instruction.trim()) return;
    const setCrafting = step === "background" ? setBgCrafting : setTfCrafting;
    const setPrompt = step === "background" ? setBgPrompt : setTfPrompt;
    setCrafting(true);
    try {
      const d = await api.post<{ prompt: string }>(
        `/api/blog/v2/articles/${articleId}/hero/craft-prompt`,
        { step, instruction: instruction.trim() },
      );
      setPrompt(d.prompt);
    } catch (e) {
      toast.err("Prompt generation failed", String(e));
    } finally {
      setCrafting(false);
    }
  }

  async function generateBackground() {
    if (!bgPrompt.trim()) return;
    setBgBusy(true);
    try {
      const d = await api.post<{ url: string; version: string }>(
        `/api/blog/v2/articles/${articleId}/hero/background`,
        { prompt: bgPrompt.trim() },
      );
      setVersions((v) => {
        const next = [...v, { url: d.url, version: d.version }];
        setSelectedIdx(next.length - 1);
        return next;
      });
      toast.ok("Hero image generated.");
    } catch (e) {
      toast.err("Image generation failed", String(e));
    } finally {
      setBgBusy(false);
    }
  }

  async function applyTransform() {
    if (!tfPrompt.trim() || versions.length === 0) return;
    const latest = versions[versions.length - 1];
    setTfBusy(true);
    try {
      const d = await api.post<{ url: string; version: string }>(
        `/api/blog/v2/articles/${articleId}/hero/transform`,
        { prompt: tfPrompt.trim(), source_url: latest.url },
      );
      setVersions((v) => {
        const next = [...v, { url: d.url, version: d.version }];
        setSelectedIdx(next.length - 1);
        return next;
      });
      toast.ok("Transform applied to the latest image.");
    } catch (e) {
      toast.err("Apply failed", String(e));
    } finally {
      setTfBusy(false);
    }
  }

  async function adoptHero() {
    if (selectedIdx < 0 || !versions[selectedIdx]) return;
    const chosen = versions[selectedIdx];
    setAdopting(true);
    try {
      await api.post(`/api/blog/v2/articles/${articleId}/hero/adopt`, { url: chosen.url });
      toast.ok("Adopted as hero.");
      onClose();
      await onAdopted();
    } catch (e) {
      toast.err("Adopt failed", String(e));
    } finally {
      setAdopting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      icon={<Wand2 className="w-4 h-4" />}
      title="Hero image · Studio"
      description="Simple instruction → perfect prompt (editable) → generate."
      footer={
        <>
          <span className="mr-auto text-xs text-ink-50/50">
            {versions.length > 0
              ? `${versions.length} version${versions.length === 1 ? "" : "s"}`
              : "no image yet"}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={adoptHero} loading={adopting} disabled={selectedIdx < 0}>
            Adopt as hero
          </Button>
        </>
      }
    >
      <div className="space-y-5 text-sm">
        <section className="space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <StepNo n={1} /> Background image
          </h3>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-50/70">
            Simple instruction
            <div className="relative">
              <input
                className={`${SELECT_CLS} w-full ${bgSuggesting ? "pr-9" : ""}`}
                value={bgInstruction}
                onChange={(e) => setBgInstruction(e.target.value)}
                placeholder={
                  bgSuggesting ? "Loading suggestion …" : "e.g. a woman on a cold, bright winter morning"
                }
              />
              {bgSuggesting && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-ink-50/40" />
              )}
            </div>
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => craftHeroPrompt("background")}
              disabled={bgCrafting || !bgInstruction.trim()}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-cream-200/60 dark:hover:bg-ink-600/40 disabled:opacity-50"
            >
              {bgCrafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {bgCrafting ? "generating …" : "Generate prompt"}
            </button>
          </div>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-50/70">
            Prompt (editable)
            <textarea
              className={`${SELECT_CLS} w-full min-h-[92px] font-mono text-[12px] leading-snug`}
              value={bgPrompt}
              onChange={(e) => setBgPrompt(e.target.value)}
              placeholder="The crafted prompt appears here — editable before generating."
            />
          </label>
          <div className="flex justify-end">
            <Button onClick={generateBackground} loading={bgBusy} disabled={!bgPrompt.trim()}>
              Generate image
            </Button>
          </div>
        </section>

        <section className="space-y-2 border-t border-cream-300 dark:border-ink-600 pt-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <StepNo n={2} /> Transform / topic alignment
          </h3>
          <p className="text-xs text-ink-50/50">
            Applied to the <strong>latest</strong> image from step 1.
          </p>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-50/70">
            Simple instruction
            <input
              className={SELECT_CLS}
              value={tfInstruction}
              onChange={(e) => setTfInstruction(e.target.value)}
              disabled={versions.length === 0}
              placeholder="e.g. turn it into a snowy city street"
            />
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => craftHeroPrompt("transform")}
              disabled={tfCrafting || !tfInstruction.trim() || versions.length === 0}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-cream-200/60 dark:hover:bg-ink-600/40 disabled:opacity-50"
            >
              {tfCrafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {tfCrafting ? "generating …" : "Generate prompt"}
            </button>
          </div>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-50/70">
            Edit prompt (editable)
            <textarea
              className={`${SELECT_CLS} w-full min-h-[92px] font-mono text-[12px] leading-snug`}
              value={tfPrompt}
              onChange={(e) => setTfPrompt(e.target.value)}
              disabled={versions.length === 0}
              placeholder="The crafted edit prompt appears here — editable before applying."
            />
          </label>
          <div className="flex justify-end">
            <Button onClick={applyTransform} loading={tfBusy} disabled={!tfPrompt.trim() || versions.length === 0}>
              Apply to latest image
            </Button>
          </div>
        </section>

        <section className="space-y-2 border-t border-cream-300 dark:border-ink-600 pt-4">
          <h3 className="text-sm font-semibold">Preview</h3>
          {versions.length === 0 || selectedIdx < 0 ? (
            <div className="rounded-xl border border-dashed border-cream-300 dark:border-ink-600 p-6 text-center text-xs text-ink-50/50">
              No image yet — generate in step 1 above.
            </div>
          ) : (
            <>
              <div className="rounded-xl overflow-hidden border border-cream-300 dark:border-ink-600 bg-cream-50 dark:bg-ink-900">
                <img
                  src={versions[selectedIdx].url}
                  alt={`Hero image version ${selectedIdx + 1} for ${articleTitle}`}
                  className="w-full object-contain max-h-[320px]"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {versions.map((v, i) => (
                  <button
                    key={v.version}
                    type="button"
                    onClick={() => setSelectedIdx(i)}
                    title={`Version ${i + 1}`}
                    className={`relative shrink-0 rounded-lg overflow-hidden border-2 ${
                      i === selectedIdx
                        ? "border-brand-500"
                        : "border-transparent hover:border-cream-300 dark:hover:border-ink-600"
                    }`}
                  >
                    <img src={v.url} alt="" className="h-14 w-20 object-cover" />
                    <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[10px] px-1 rounded-tl">
                      v{i + 1}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </Modal>
  );
}

/**
 * Image edit panel — right rail / drawer (deliberate exception to "modals
 * centered": the user asked for a right-side image editor). Edits ONE
 * in-article image: simple instruction → prompt (editable) → transform/generate
 * → preview + versions → replace in the article.
 */
export function ImageEditPanel({
  image,
  articleId,
  onClose,
  onReplaced,
}: {
  image: { url: string; slot_type?: string };
  articleId: string;
  onClose: () => void;
  onReplaced: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<"transform" | "generate">("transform");
  const [instruction, setInstruction] = useState("");
  const [crafting, setCrafting] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState<{ url: string; version: string }[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [replacing, setReplacing] = useState(false);

  const previewUrl =
    selectedIdx >= 0 && versions[selectedIdx] ? versions[selectedIdx].url : image.url;

  async function craft() {
    if (!instruction.trim()) return;
    setCrafting(true);
    try {
      const step = mode === "transform" ? "transform" : "background";
      const d = await api.post<{ prompt: string }>(
        `/api/blog/v2/articles/${articleId}/hero/craft-prompt`,
        { step, instruction: instruction.trim() },
      );
      setPrompt(d.prompt);
    } catch (e) {
      toast.err("Prompt generation failed", String(e));
    } finally {
      setCrafting(false);
    }
  }

  async function run() {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { mode, prompt: prompt.trim() };
      if (mode === "transform") body.source_url = image.url;
      const d = await api.post<{ url: string; version: string }>(
        `/api/blog/v2/articles/${articleId}/images/edit`,
        body,
      );
      setVersions((v) => {
        const next = [...v, { url: d.url, version: d.version }];
        setSelectedIdx(next.length - 1);
        return next;
      });
      toast.ok(mode === "transform" ? "Image transformed." : "Image generated.");
    } catch (e) {
      toast.err("Image edit failed", String(e));
    } finally {
      setBusy(false);
    }
  }

  async function replace() {
    if (selectedIdx < 0 || !versions[selectedIdx]) return;
    setReplacing(true);
    try {
      await api.post(`/api/blog/v2/articles/${articleId}/images/replace`, {
        old_url: image.url,
        new_url: versions[selectedIdx].url,
      });
      toast.ok("Replaced in the article.");
      await onReplaced();
    } catch (e) {
      toast.err("Replace failed", String(e));
    } finally {
      setReplacing(false);
    }
  }

  const runLabel = mode === "transform" ? "Transform image" : "Generate image";
  const instrPlaceholder =
    mode === "transform"
      ? "e.g. turn it into a snowy street"
      : "e.g. a woman on a bright winter morning";

  return (
    <>
      <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-50 w-[min(92vw,400px)] max-h-screen lg:static lg:inset-y-auto lg:right-auto lg:z-auto lg:w-auto lg:max-h-none overflow-y-auto rounded-l-2xl lg:rounded-2xl border border-cream-300 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 shadow-2xl lg:shadow-sm p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Wand2 className="w-4 h-4" /> Edit image
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-cream-200/60 dark:hover:bg-ink-600/40"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="rounded-xl overflow-hidden border border-cream-300 dark:border-ink-600 bg-cream-50 dark:bg-ink-900 mb-3">
          <img src={previewUrl} alt="Image preview" className="w-full object-contain max-h-56" />
        </div>

        <div className="flex gap-1 mb-3 text-xs">
          {(["transform", "generate"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 rounded-lg px-2 py-1.5 border ${
                mode === m
                  ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 font-medium"
                  : "border-cream-300 dark:border-ink-600 hover:bg-cream-200/50 dark:hover:bg-ink-600/30"
              }`}
            >
              {m === "transform" ? "Transform" : "Generate"}
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-1 text-xs font-medium text-ink-50/70 mb-2">
          Simple instruction
          <input
            className={`${SELECT_CLS} w-full`}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder={instrPlaceholder}
          />
        </label>
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={craft}
            disabled={crafting || !instruction.trim()}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-cream-200/60 dark:hover:bg-ink-600/40 disabled:opacity-50"
          >
            {crafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {crafting ? "generating …" : "Generate prompt"}
          </button>
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-50/70 mb-2">
          Prompt (editable)
          <textarea
            className={`${SELECT_CLS} w-full min-h-[88px] font-mono text-[12px] leading-snug`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Your instruction crafts the prompt here — editable before generating."
          />
        </label>
        <div className="flex justify-end mb-3">
          <Button onClick={run} loading={busy} disabled={!prompt.trim()}>
            {runLabel}
          </Button>
        </div>

        {versions.length > 0 && (
          <div className="border-t border-cream-300 dark:border-ink-600 pt-3 mb-3">
            <div className="text-xs font-medium text-ink-50/70 mb-1.5">Versions</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setSelectedIdx(-1)}
                title="Original"
                className={`relative shrink-0 rounded-lg overflow-hidden border-2 ${
                  selectedIdx === -1
                    ? "border-brand-500"
                    : "border-transparent hover:border-cream-300 dark:hover:border-ink-600"
                }`}
              >
                <img src={image.url} alt="" className="h-14 w-20 object-cover" />
                <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[10px] px-1 rounded-tl">
                  Orig
                </span>
              </button>
              {versions.map((v, i) => (
                <button
                  key={v.version}
                  type="button"
                  onClick={() => setSelectedIdx(i)}
                  title={`Version ${i + 1}`}
                  className={`relative shrink-0 rounded-lg overflow-hidden border-2 ${
                    i === selectedIdx
                      ? "border-brand-500"
                      : "border-transparent hover:border-cream-300 dark:hover:border-ink-600"
                  }`}
                >
                  <img src={v.url} alt="" className="h-14 w-20 object-cover" />
                  <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[10px] px-1 rounded-tl">
                    v{i + 1}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={replace}
          loading={replacing}
          disabled={selectedIdx < 0}
          className="w-full justify-center"
        >
          Replace in the article
        </Button>
      </aside>
    </>
  );
}
